import { useState, useEffect, useCallback } from 'react';

/**
 * usePermissions Hook
 * 
 * Smart permission manager that:
 * 1. Checks browser permission state BEFORE requesting
 * 2. Checks localStorage for previous grants
 * 3. Only requests permissions once
 * 4. Provides unified permission status for camera and location
 * 
 * 🔧 FIX: Skips permission checks on /admin routes to prevent prompts
 * 
 * Usage:
 *   const { cameraGranted, locationGranted, requestCamera, requestLocation } = usePermissions();
 */

const usePermissions = () => {
  const [cameraGranted, setCameraGranted] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);
  const [cameraChecked, setCameraChecked] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false);

  // 🔧 FIX: DON'T check permissions at all - only when explicitly requested
  // The old code was checking on mount, which happens on ALL pages including admin
  
  // REMOVED automatic permission checks on mount
  // Now permissions are only checked when:
  // 1. User explicitly opens camera (requestCamera called)
  // 2. GPS is needed for submission (getCurrentLocation called)

  /**
   * Check if camera permission is already granted
   * Checks both browser Permissions API and localStorage
   */
  const checkCameraPermission = useCallback(async () => {
    try {
      // Check localStorage first (fast)
      const localStorageGranted = localStorage.getItem('cameraPermissionGranted') === 'true';
      
      // Check browser Permissions API if supported
      if ('permissions' in navigator) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' });
          const browserGranted = result.state === 'granted';
          
          // Update state based on actual browser permission
          setCameraGranted(browserGranted);
          
          // Keep localStorage in sync
          if (browserGranted) {
            localStorage.setItem('cameraPermissionGranted', 'true');
          }
          
          // Listen for permission changes
          result.addEventListener('change', () => {
            setCameraGranted(result.state === 'granted');
            localStorage.setItem('cameraPermissionGranted', result.state === 'granted' ? 'true' : 'false');
          });
          
          setCameraChecked(true);
          return browserGranted;
        } catch (permissionQueryError) {
          // Permissions API not supported for camera on this browser
          // Fall back to localStorage
          console.log('[Permissions] Camera query not supported, using localStorage');
          setCameraGranted(localStorageGranted);
          setCameraChecked(true);
          return localStorageGranted;
        }
      } else {
        // Permissions API not supported at all
        setCameraGranted(localStorageGranted);
        setCameraChecked(true);
        return localStorageGranted;
      }
    } catch (error) {
      console.error('[Permissions] Error checking camera permission:', error);
      setCameraChecked(true);
      return false;
    }
  }, []);

  /**
   * Check if location permission is already granted
   * Checks both browser Permissions API and localStorage
   */
  const checkLocationPermission = useCallback(async () => {
    try {
      // Check localStorage first (fast)
      const localStorageGranted = localStorage.getItem('locationPermissionGranted') === 'true';
      
      // Check browser Permissions API if supported
      if ('permissions' in navigator) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          const browserGranted = result.state === 'granted';
          
          // Update state based on actual browser permission
          setLocationGranted(browserGranted);
          
          // Keep localStorage in sync
          if (browserGranted) {
            localStorage.setItem('locationPermissionGranted', 'true');
          }
          
          // Listen for permission changes
          result.addEventListener('change', () => {
            setLocationGranted(result.state === 'granted');
            localStorage.setItem('locationPermissionGranted', result.state === 'granted' ? 'true' : 'false');
          });
          
          setLocationChecked(true);
          return browserGranted;
        } catch (permissionQueryError) {
          // Permissions API not supported for geolocation on this browser
          // Fall back to localStorage
          console.log('[Permissions] Geolocation query not supported, using localStorage');
          setLocationGranted(localStorageGranted);
          setLocationChecked(true);
          return localStorageGranted;
        }
      } else {
        // Permissions API not supported at all
        setLocationGranted(localStorageGranted);
        setLocationChecked(true);
        return localStorageGranted;
      }
    } catch (error) {
      console.error('[Permissions] Error checking location permission:', error);
      setLocationChecked(true);
      return false;
    }
  }, []);

  /**
   * Request camera permission (only if not already granted)
   * Returns: true if granted, false if denied
   */
  const requestCamera = useCallback(async () => {
    try {
      console.log('📷 [usePermissions] requestCamera() called');
      console.log('📷 [usePermissions] Current state - cameraGranted:', cameraGranted);
      
      // Check if already granted first
      console.log('📷 [usePermissions] Checking current permission state...');
      const alreadyGranted = await checkCameraPermission();
      if (alreadyGranted) {
        console.log('📷 ✅ [usePermissions] Camera ALREADY GRANTED, not requesting again!');
        return true;
      }
      
      console.log('📷 [usePermissions] Camera not granted yet, calling getUserMedia()...');
      // Request permission via getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Stop stream immediately after permission granted
      stream.getTracks().forEach(track => track.stop());
      console.log('📷 [usePermissions] Stream obtained and stopped');
      
      // Save to localStorage
      localStorage.setItem('cameraPermissionGranted', 'true');
      setCameraGranted(true);
      
      console.log('📷 ✅ [usePermissions] Camera permission GRANTED and saved!');
      return true;
    } catch (error) {
      console.error('📷 ❌ [usePermissions] Camera permission DENIED:', error.name);
      localStorage.setItem('cameraPermissionGranted', 'false');
      setCameraGranted(false);
      return false;
    }
  }, [checkCameraPermission, cameraGranted]);

  /**
   * Request location permission (only if not already granted)
   * Returns: Promise<boolean> - true if granted, false if denied
   */
  const requestLocation = useCallback(async () => {
    return new Promise(async (resolve) => {
      try {
        console.log('📍 [usePermissions] requestLocation() called');
        console.log('📍 [usePermissions] Current state - locationGranted:', locationGranted);
        
        // Check if already granted first
        console.log('📍 [usePermissions] Checking current permission state...');
        const alreadyGranted = await checkLocationPermission();
        if (alreadyGranted) {
          console.log('📍 ✅ [usePermissions] Location ALREADY GRANTED, not requesting again!');
          resolve(true);
          return;
        }
        
        if (!navigator.geolocation) {
          console.error('📍 [usePermissions] Geolocation not supported');
          resolve(false);
          return;
        }

        console.log('📍 [usePermissions] Location not granted yet, calling getCurrentPosition()...');
        navigator.geolocation.getCurrentPosition(
          () => {
            // Success - permission granted
            localStorage.setItem('locationPermissionGranted', 'true');
            setLocationGranted(true);
            console.log('📍 ✅ [usePermissions] Location permission GRANTED and saved!');
            resolve(true);
          },
          (error) => {
            // Error - permission denied or error
            console.error('📍 ❌ [usePermissions] Location permission DENIED:', error.message);
            localStorage.setItem('locationPermissionGranted', 'false');
            setLocationGranted(false);
            resolve(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 60000,  // 60 seconds - give GPS plenty of time (indoor, thick walls, etc.)
            maximumAge: 0
          }
        );
      } catch (error) {
        console.error('📍 [usePermissions] Error requesting location:', error);
        resolve(false);
      }
    });
  }, [checkLocationPermission, locationGranted]);

  /**
   * Get current location (only if permission granted)
   * Returns: Promise<GeolocationPosition | null>
   */
  const getCurrentLocation = useCallback(async () => {
    return new Promise(async (resolve) => {
      try {
        // Check if permission is granted
        if (!locationGranted) {
          console.log('[Permissions] Location not granted, requesting...');
          const granted = await requestLocation();
          if (!granted) {
            resolve(null);
            return;
          }
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (error) => {
            console.error('[Permissions] Error getting location:', error.message);
            resolve(null);
          },
          {
            enableHighAccuracy: true,
            timeout: 60000,  // 60 seconds - submission takes time, GPS should too
            maximumAge: 120000 // Accept cached position up to 2 minutes old
          }
        );
      } catch (error) {
        console.error('[Permissions] Error in getCurrentLocation:', error);
        resolve(null);
      }
    });
  }, [locationGranted, requestLocation]);

  /**
   * Reset all permissions (for testing or logout)
   */
  const resetPermissions = useCallback(() => {
    localStorage.removeItem('cameraPermissionGranted');
    localStorage.removeItem('locationPermissionGranted');
    setCameraGranted(false);
    setLocationGranted(false);
    console.log('[Permissions] All permissions reset');
  }, []);

  return {
    // Permission states
    cameraGranted,
    locationGranted,
    cameraChecked,
    locationChecked,
    
    // Request methods (smart - won't ask twice)
    requestCamera,
    requestLocation,
    
    // Utility methods
    checkCameraPermission,
    checkLocationPermission,
    getCurrentLocation,
    resetPermissions
  };
};

export default usePermissions;

